import PropTypes from 'prop-types'; // @mui
import Box from '@mui/material/Box';
// hooks
import {useResponsive} from 'packages/common/hooks/useResponsive';
import {useAdminBannerOffset} from 'packages/common/hooks/useAdminBannerOffset';
// components
import {useSettingsContext} from 'packages/common/components/settings';
import {SubscriptionNoticeBanner} from 'packages/common/components/subscription-notice-banner';
//
import {NAV, CONTENT_PADDING, SAFE_AREA_X, SAFE_AREA_TOP} from '../config-layout';
import {MOBILE_HEADER_HEIGHT} from './mobile-header';
import {BOTTOM_NAV_HEIGHT} from './bottom-nav';

// ----------------------------------------------------------------------

const SPACING = 12;

// Menu horizontal (desktop) : sa barre est en flux au-dessus du contenu, ce
// retrait laisse respirer le contenu sous elle.
const HORIZONTAL_NAV_TOP_SPACING = 40;

/**
 * Zone de contenu du dashboard : largeur et retraits selon le support et le menu.
 *
 * Sous `lg` : pleine largeur, hauteur du header mobile réservée en haut, barre
 * du bas réservée en bas. À partir de `lg` : largeur réduite du menu latéral
 * (vertical ou mini) ou pleine largeur sous le menu horizontal.
 *
 * @param {Object} props - Props du composant
 * @param {React.ReactNode} props.children - Contenu de la page
 * @param {Object} [props.sx] - Surcharges ponctuelles
 * @param {boolean} [props.fullHeight=false] - Page à hauteur fixe avec défilement interne
 * @return {JSX.Element} Conteneur principal
 */
export default function Main({children, sx, fullHeight = false, ...other}) {
  const settings = useSettingsContext();
  const adminBannerOffset = useAdminBannerOffset();

  const lgUp = useResponsive('up', 'lg');

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  // Largeur occupée par le menu latéral à partir de lg (0 pour le menu horizontal)
  const desktopNavWidth = isNavHorizontal ? 0 : settings.themeLayout === 'mini' ? NAV.W_MINI : NAV.W_VERTICAL;

  // Retrait haut desktop : sous le menu horizontal, l'espace de sa barre ;
  // sinon l'espacement standard (nul en pleine hauteur, chaque pixel compte).
  const desktopTopSpacing = isNavHorizontal ? HORIZONTAL_NAV_TOP_SPACING : (fullHeight ? 0 : SPACING);

  // Structure unifiée avec box intermédiaire pour gérer le padding-top
  // Mode fullHeight : hauteur fixe 100vh
  // Mode normal : hauteur flexible avec padding bottom
  return (
    <Box
      component="main"
      sx={{
        // Layout commun
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'grey.100',

        // Retraits materiels : en paysage l'encoche mord sur les cotes.
        // A partir de lg le menu vertical occupe deja le bord gauche.
        pl: {xs: SAFE_AREA_X.LEFT, lg: 0},
        pr: SAFE_AREA_X.RIGHT,

        // Mode fullHeight : hauteur fixe
        ...(fullHeight && {
          'flexGrow': 0,
          'flexShrink': 0,
          'flexBasis': 'auto',
          'height': '100vh',
          'maxHeight': '100vh',
          '@supports (-webkit-touch-callout: none)': {
            height: '-webkit-fill-available',
            maxHeight: '-webkit-fill-available',
          },
        }),

        // Mode normal : hauteur flexible
        ...(!fullHeight && {
          flexGrow: 1,
          minHeight: 1,
          overflow: 'visible',
        }),

        // Largeur adaptée au menu latéral à partir de lg
        ...(lgUp && desktopNavWidth > 0 && {
          width: `calc(100% - ${desktopNavWidth}px)`,
        }),

        ...sx,
      }}
      {...other}
    >
      {/* Box intermédiaire : gère le padding-top et bottom de manière cohérente */}
      <Box
        sx={{
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? undefined : 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: fullHeight ? 'hidden' : 'visible',
          boxSizing: 'border-box',

          // Padding horizontal responsive (source unique pour tout le dashboard)
          px: CONTENT_PADDING,

          // Padding top : mobile header + safe area sur mobile, spacing sur desktop.
          // Même seuil que l'affichage du header mobile (`lg` dans layout.js) :
          // au-delà, plus de header, donc plus d'espace à lui réserver. Le retrait
          // haut reste nécessaire en desktop : un iPad en PWA plein écran garde ce
          // layout et sa barre d'état mord sur le haut de page.
          pt: {
            xs: `calc(${MOBILE_HEADER_HEIGHT + (fullHeight ? 0 : SPACING) + adminBannerOffset}px + ${SAFE_AREA_TOP})`,
            lg: `calc(${desktopTopSpacing + adminBannerOffset}px + ${SAFE_AREA_TOP})`,
          },

          // Padding bottom : bottom nav + safe area sur mobile, spacing sur desktop
          // Meme en fullHeight, il faut reserver l'espace pour la bottom nav mobile
          pb: {
            xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px)${fullHeight ? '' : ` + ${SPACING}px`})`,
            lg: fullHeight ? 0 : `${SPACING}px`,
          },
        }}
      >
        <SubscriptionNoticeBanner />
        {children}
      </Box>
    </Box>
  );
}

Main.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  fullHeight: PropTypes.bool,
};
